/**
 * useSessionExpiration hook for handling session expiration during app use.
 *
 * This hook monitors for 401 responses and handles session expiration gracefully:
 * - Attempts silent token refresh when session expires
 * - Shows re-auth modal if refresh fails
 * - Preserves current URL for return after re-authentication
 * - Integrates with the API client interceptor
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { refreshToken, login, isAuthenticated, clearAllUserData } from '@/lib/keycloak';
import { useAuthStore } from '@/stores/authStore';

/**
 * Session expiration state
 */
export interface SessionExpirationState {
  /** Whether the session has expired and needs re-authentication */
  isExpired: boolean;
  /** Whether a silent refresh is in progress */
  isRefreshing: boolean;
  /** Whether the re-auth modal should be shown */
  showReauthModal: boolean;
  /** The URL to return to after re-authentication */
  returnUrl: string | null;
}

/**
 * Session expiration event for cross-component communication
 */
export interface SessionExpirationEvent {
  type: 'session-expired';
  returnUrl: string;
}

/** Global event name for session expiration */
export const SESSION_EXPIRED_EVENT = 'rosie:session-expired';

/**
 * Dispatch a session expired event.
 * Can be called from API interceptors or other parts of the app.
 */
export function dispatchSessionExpiredEvent(returnUrl?: string): void {
  const event = new CustomEvent<SessionExpirationEvent>(SESSION_EXPIRED_EVENT, {
    detail: {
      type: 'session-expired',
      returnUrl: returnUrl ?? window.location.href,
    },
  });
  window.dispatchEvent(event);
}

/**
 * Options for the useSessionExpiration hook
 */
export interface UseSessionExpirationOptions {
  /** Callback when re-authentication is required */
  onReauthRequired?: () => void;
  /** Callback when silent refresh succeeds */
  onRefreshSuccess?: () => void;
  /** Callback when silent refresh fails */
  onRefreshFailed?: () => void;
}

/**
 * Return type for the useSessionExpiration hook
 */
export interface UseSessionExpirationReturn {
  /** Current session expiration state */
  state: SessionExpirationState;
  /** Handle session expiration (attempt refresh, then show modal) */
  handleSessionExpired: () => Promise<boolean>;
  /** Dismiss the re-auth modal and redirect to login */
  redirectToLogin: () => void;
  /** Dismiss the re-auth modal without action */
  dismissModal: () => void;
  /** Reset the expiration state */
  reset: () => void;
}

/**
 * Hook for handling session expiration during app use.
 *
 * @param options - Configuration options
 * @returns Session expiration state and handlers
 *
 * @example
 * ```tsx
 * function AppLayout() {
 *   const {
 *     state,
 *     handleSessionExpired,
 *     redirectToLogin,
 *     dismissModal,
 *   } = useSessionExpiration({
 *     onReauthRequired: () => console.log('Re-auth needed'),
 *   });
 *
 *   useEffect(() => {
 *     // Listen for session expiration from API interceptor
 *     const handler = () => handleSessionExpired();
 *     window.addEventListener('rosie:session-expired', handler);
 *     return () => window.removeEventListener('rosie:session-expired', handler);
 *   }, [handleSessionExpired]);
 *
 *   return (
 *     <>
 *       <MainContent />
 *       {state.showReauthModal && (
 *         <ReauthModal
 *           onLogin={redirectToLogin}
 *           onDismiss={dismissModal}
 *         />
 *       )}
 *     </>
 *   );
 * }
 * ```
 */
export function useSessionExpiration(
  options: UseSessionExpirationOptions = {}
): UseSessionExpirationReturn {
  const { onReauthRequired, onRefreshSuccess, onRefreshFailed } = options;

  const [state, setState] = useState<SessionExpirationState>({
    isExpired: false,
    isRefreshing: false,
    showReauthModal: false,
    returnUrl: null,
  });

  const refreshAttemptedRef = useRef(false);
  const { updateFromKeycloak } = useAuthStore();

  /**
   * Attempt to silently refresh the token.
   * Returns true if refresh succeeded, false otherwise.
   */
  const attemptSilentRefresh = useCallback(async (): Promise<boolean> => {
    if (refreshAttemptedRef.current) {
      return false;
    }

    refreshAttemptedRef.current = true;
    setState((prev) => ({ ...prev, isRefreshing: true }));

    try {
      // Attempt token refresh with a longer validity requirement
      const refreshed = await refreshToken(300); // 5 minutes

      if (refreshed && isAuthenticated()) {
        updateFromKeycloak();
        setState((prev) => ({
          ...prev,
          isRefreshing: false,
          isExpired: false,
          showReauthModal: false,
        }));
        refreshAttemptedRef.current = false;
        onRefreshSuccess?.();
        return true;
      }
    } catch (error) {
      console.warn('Silent token refresh failed:', error);
    }

    setState((prev) => ({ ...prev, isRefreshing: false }));
    refreshAttemptedRef.current = false;
    return false;
  }, [updateFromKeycloak, onRefreshSuccess]);

  /**
   * Handle session expiration.
   * First attempts silent refresh, then shows modal if refresh fails.
   */
  const handleSessionExpired = useCallback(async (): Promise<boolean> => {
    // Store current URL for return after re-auth
    const currentUrl = window.location.href;

    setState((prev) => ({
      ...prev,
      isExpired: true,
      returnUrl: currentUrl,
    }));

    // Attempt silent refresh first
    const refreshSucceeded = await attemptSilentRefresh();

    if (refreshSucceeded) {
      return true;
    }

    // Silent refresh failed, so the session is definitively over. Clear the same
    // data an explicit logout clears — cached API responses, drafts and the
    // non-prefixed credentials — before showing the modal. The user may dismiss
    // it and stay on the page indefinitely, so this must not wait for a click.
    await clearAllUserData();

    // Show re-auth modal if refresh failed
    setState((prev) => ({
      ...prev,
      showReauthModal: true,
    }));

    onReauthRequired?.();
    onRefreshFailed?.();
    return false;
  }, [attemptSilentRefresh, onReauthRequired, onRefreshFailed]);

  /**
   * Redirect to login page, preserving return URL.
   */
  const redirectToLogin = useCallback(() => {
    // Store return URL in session storage for post-login redirect
    if (state.returnUrl) {
      sessionStorage.setItem('rosie:return-url', state.returnUrl);
    }

    setState((prev) => ({
      ...prev,
      showReauthModal: false,
    }));

    // Trigger Keycloak login
    login();
  }, [state.returnUrl]);

  /**
   * Dismiss the re-auth modal without redirecting.
   */
  const dismissModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showReauthModal: false,
    }));
  }, []);

  /**
   * Reset the expiration state.
   */
  const reset = useCallback(() => {
    setState({
      isExpired: false,
      isRefreshing: false,
      showReauthModal: false,
      returnUrl: null,
    });
    refreshAttemptedRef.current = false;
  }, []);

  // Listen for session expiration events from API interceptor
  useEffect(() => {
    function handleEvent(event: Event) {
      const customEvent = event as CustomEvent<SessionExpirationEvent>;
      if (customEvent.detail.returnUrl) {
        setState((prev) => ({
          ...prev,
          returnUrl: customEvent.detail.returnUrl,
        }));
      }
      void handleSessionExpired();
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleEvent);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleEvent);
    };
  }, [handleSessionExpired]);

  // Check for return URL after authentication
  useEffect(() => {
    const returnUrl = sessionStorage.getItem('rosie:return-url');
    if (returnUrl && isAuthenticated()) {
      sessionStorage.removeItem('rosie:return-url');
      // Navigate to the stored return URL
      window.history.replaceState(null, '', returnUrl);
    }
  }, []);

  return {
    state,
    handleSessionExpired,
    redirectToLogin,
    dismissModal,
    reset,
  };
}

export default useSessionExpiration;
